import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Search, Menu, User as UserIcon, Settings as SettingsIcon, Bell, LogOut, ShieldCheck, Megaphone, HelpCircle } from 'lucide-react';
import axios from 'axios';
import { isAdmin } from '@/lib/adminAuth';
import { LogoIcon } from '@/components/Logo';
import GlobalSearch from '@/components/GlobalSearch';
import WebSideMenu from '@/components/WebSideMenu';

// True when this build is running inside the iOS / Android Capacitor shell.
// We use it to gate the web-only hamburger menu so the native apps keep
// using their bottom tab bar exclusively.
const IS_NATIVE_APP = (() => {
  try {
    if (typeof window === 'undefined') return false;
    const cap = window.Capacitor;
    if (!cap) return false;
    // Capacitor exposes either `isNativePlatform()` (newer) or `isNative` (older).
    if (typeof cap.isNativePlatform === 'function') return cap.isNativePlatform();
    return !!cap.isNative || ['ios', 'android'].includes(cap.getPlatform && cap.getPlatform());
  } catch { return false; }
})();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ===== Shared tokens =====
export const ease = [0.16, 1, 0.3, 1];
export const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease } },
};

// Top-bar nav links. `Explore` was merged into Home and `Prompts` was merged
// into Trending — keep only the consolidated destinations here so the desktop
// nav matches the mobile bottom tab bar.
// "Following" is intentionally NOT in the top bar — it lives in the side
// drawer only.  The top bar is reserved for primary public destinations.
export const NAV_LINKS = [
  { label: 'Home', to: '/' },
  { label: 'Zenflow', to: '/zenflow' },
  { label: 'Create', to: '/create', requiresAuth: true },
  { label: 'Trending', to: '/trending' },
  { label: 'About', to: '/about' },
];

// ===== Decorative primitives =====
export function GrainOverlay() {
  return (
    <div aria-hidden className="pointer-events-none fixed inset-0 z-0 opacity-[0.05] mix-blend-overlay" style={{
      backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.7'/></svg>")`,
    }} />
  );
}

export function Glow({ className = '', color = '#1C4749', opacity = 0.18 }) {
  return <div aria-hidden className={`absolute pointer-events-none rounded-full blur-3xl ${className}`} style={{ backgroundColor: color, opacity }} />;
}

export const Reveal = ({ children, delay = 0, className = '' }) => (
  <motion.div
    variants={fadeUp}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, margin: '-80px' }}
    transition={{ duration: 0.8, ease, delay }}
    className={className}
  >
    {children}
  </motion.div>
);

export const Overline = ({ children, testId, className = '' }) => (
  <span data-testid={testId} className={`inline-block text-[11px] tracking-[0.24em] uppercase font-bold text-[#1C4749] ${className}`}>{children}</span>
);


/**
 * Inline search affordance that lives inside the top nav.
 *
 * Collapsed (default): a pill-sized button showing just the magnifier icon.
 * Expanded (after click/focus): a wider text input the user can type into.
 *   - Pressing Enter or clicking the magnifier submits and forwards to the
 *     full-screen `SearchOverlay` via `onOpen()` (which already exists and
 *     drives the existing /api/search experience).
 *   - Pressing Escape or clicking outside collapses without losing the value.
 *
 * Designed for both mobile and desktop: the expanded width is clamped so the
 * other nav controls stay visible.
 */
function ExpandableSearch({ onOpen }) {
  const [expanded, setExpanded] = useState(false);
  const [value, setValue] = useState('');
  const inputRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (expanded) {
      const t = setTimeout(() => inputRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [expanded]);

  useEffect(() => {
    if (!expanded) return undefined;
    const onClickAway = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setExpanded(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setExpanded(false); };
    document.addEventListener('mousedown', onClickAway);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClickAway);
      document.removeEventListener('keydown', onKey);
    };
  }, [expanded]);

  const submit = () => {
    if (!value.trim()) {
      // Empty submit just opens the overlay so users can browse trending searches.
      onOpen && onOpen();
      return;
    }
    onOpen && onOpen(value.trim());
    setExpanded(false);
  };

  return (
    <div
      ref={wrapRef}
      data-testid="nav-search"
      className={`flex items-center gap-1 rounded-full border border-white/10 bg-white/5 backdrop-blur-md transition-[width,background] duration-300 ${expanded ? 'w-[200px] sm:w-[260px] md:w-[320px] bg-white/10' : 'w-11'}`}
    >
      <button
        data-testid="nav-search-btn"
        onClick={() => (expanded ? submit() : setExpanded(true))}
        className="shrink-0 w-11 h-11 rounded-full text-white/80 hover:text-white flex items-center justify-center"
        title="Search (⌘K)"
        aria-label="Search"
        aria-expanded={expanded}
      >
        <Search className="w-5 h-5" />
      </button>
      <input
        ref={inputRef}
        data-testid="nav-search-input"
        type="search"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
        placeholder="Search ZenScroll…"
        aria-label="Search ZenScroll"
        tabIndex={expanded ? 0 : -1}
        // text-base (16px) prevents iOS Safari from auto-zooming the page on
        // focus.  Visual rendering inside the small pill still looks tight
        // because the input is shrink-to-fit, but we never drop below the
        // iOS auto-zoom threshold.
        className={`min-w-0 bg-transparent text-base text-white placeholder:text-white/50 outline-none transition-[opacity,padding] duration-200 ${expanded ? 'opacity-100 pr-3 flex-1' : 'opacity-0 w-0 p-0'}`}
      />
    </div>
  );
}

// ===== Nav =====
export function Nav({ navigate, token, staticHeader = false, onLogout }) {
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  // `open` mobile-sheet state removed when the hamburger menu was dropped.
  const [searchOpen, setSearchOpen] = useState(false);
  // Web-only side menu — never shown inside the Capacitor iOS/Android apps.
  const [menuOpen, setMenuOpen] = useState(false);

  // Compute which top-nav link is "active" based on the current route.
  // Returns the matching link's `to` value (or `null` if no match).
  // Notes:
  //   - `/` and `/welcome` and `/home` all map to the "Home" link.
  //   - `/following` lives under the Following entry (Zenflow's tab routing
  //     keeps "/following" intact in `pathname`).
  //   - Other entries match by exact prefix on `pathname`.
  const activeHref = (() => {
    if (pathname === '/' || pathname === '/welcome' || pathname === '/home') return '/';
    const ordered = [
      '/zenflow', '/create', '/trending', '/about',
    ];
    return ordered.find((p) => pathname === p || pathname.startsWith(`${p}/`)) || null;
  })();

  useEffect(() => {
    // Android scroll jank fix:
    //   1. `{ passive: true }` tells the browser the listener will *never*
    //      call `preventDefault`, so it can hand the scroll to the compositor
    //      immediately instead of waiting for our JS.  Without this Chrome
    //      Android delays every scroll frame ~150–250 ms.
    //   2. `requestAnimationFrame` coalesces 60–120 events/sec into one paint
    //      so a flick scroll doesn't trigger hundreds of React renders.
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        setScrolled(window.scrollY > 12);
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // Poll unread notification count when authenticated. (Notification badge
  // is no longer surfaced in the top bar — the Profile sheet on mobile owns
  // the inbox — but the count is still fetched in case other UI bits surface
  // it later, e.g. a future toast bell on desktop.)
  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    const fetchCount = async () => {
      try {
        await axios.get(`${API}/notifications/unread-count`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
      } catch {
        /* ignore */
      }
    };
    fetchCount();
    const id = setInterval(fetchCount, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [token]);

  // Z-dropdown close-on-outside-click effect removed along with the Z chip.

  const handleSignOut = () => {
    const proceed = () => {
      try {
        localStorage.removeItem('zenscroll_token');
        localStorage.removeItem('zenscroll_user');
        localStorage.removeItem('zenscroll_onboarded');
      } catch { /* ignore */ }
      // Hard reload to /auth so the App-level `token` state is fully reset
      window.location.href = '/auth';
    };
    // Ask "how did we make you feel?" first, then complete the sign-out.
    window.dispatchEvent(new CustomEvent('zenscroll:mood-survey', { detail: { onDone: proceed } }));
  };

  // `goToFromZ` removed along with the Z dropdown.

  // Global ⌘K / Ctrl+K shortcut to open search
  useEffect(() => {
    const h = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  // Top nav is now visible on every viewport (incl. mobile) and pinned to the
  // very top of the screen so the search/logo are always within reach.
  // `staticHeader` lets specific pages (e.g. the public landing) opt out of
  // the fixed-position behaviour so the header scrolls away naturally with
  // the page — important on iOS TestFlight where a frozen header read as
  // "frozen page" to first-time visitors.
  const hideOnMobile = false;
  const positionClasses = staticHeader
    ? 'relative w-full z-50'
    : 'fixed top-0 inset-x-0 z-50';

  return (
    <header
      // Transparent, floating navigation (2026-06 design-system update):
      // no solid bar, no border, no shadow block. A lightweight top-down
      // gradient scrim keeps the logo/links readable over media while the
      // nav visually blends into the page underneath. The scrim deepens
      // slightly once the user scrolls so text stays legible in motion.
      className={`${positionClasses} transition-colors duration-200 mobile-chrome-top ${
        scrolled && !staticHeader
          ? 'bg-gradient-to-b from-[#0A1514]/85 via-[#0A1514]/45 to-transparent'
          : 'bg-gradient-to-b from-[#0A1514]/50 via-[#0A1514]/25 to-transparent'
      } ${hideOnMobile ? 'hidden md:block' : ''}`}
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
    >
      {/* Compact mobile header row: 56px tall (h-14) on phones — combined
          with the single safe-area padding above, total visible height
          stays well under 90px. Desktop keeps its taller rows. */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12 h-14 sm:h-20 md:h-24 flex items-center gap-3 sm:gap-6">
        {/* Logo — teal-tinted brand mark, sits flush-left in the nav row */}
        <Link
          to={token ? '/zenflow' : '/'}
          data-testid="nav-logo"
          aria-label="ZenScroll home"
          className="flex items-center shrink-0"
        >
          <LogoIcon className="h-[34px] sm:h-[63px] md:h-[85px] w-auto" tint="#1C4749" />
        </Link>
        <nav className="hidden lg:flex items-center gap-8 ml-auto">
          {NAV_LINKS
            .filter((l) => !l.requiresAuth || token)
            .map((l) => {
              const isActive = activeHref === l.to;
              return (
                <a
                  key={l.label}
                  href={l.to}
                  data-testid={`nav-link-${l.label.toLowerCase()}`}
                  data-active={isActive ? 'true' : 'false'}
                  aria-current={isActive ? 'page' : undefined}
                  className={`relative text-sm transition-colors after:absolute after:left-0 after:right-0 after:-bottom-1.5 after:h-[2px] after:rounded-full after:transition-all ${
                    isActive
                      ? 'text-white font-semibold after:bg-[#5EEAD4] after:opacity-100'
                      : 'text-white/70 hover:text-white after:bg-[#5EEAD4] after:opacity-0 hover:after:opacity-30'
                  }`}
                >
                  {l.label}
                </a>
              );
            })}
        </nav>
        <div className="flex items-center gap-2 lg:ml-0 ml-auto">
          <ExpandableSearch onOpen={() => setSearchOpen(true)} />
          {token ? (
            // "Z" account chip and dropdown removed per design — the
            // Profile tab in MobileBottomNav now owns all secondary nav.
            null
          ) : (
            <>
              <button data-testid="nav-signin" onClick={() => navigate('/auth')} className="hidden sm:inline-block text-sm text-white/80 hover:text-white px-4 py-2">Sign In</button>
              <button data-testid="nav-signup" onClick={() => navigate('/auth?mode=signup')} className="bg-[#1C4749] text-white font-semibold rounded-full px-5 py-2 text-sm hover:bg-[#2A6F72] transition-colors shadow-[0_0_20px_rgba(28,71,73,0.3)]">Sign Up</button>
            </>
          )}
          {/* Web-only hamburger.  Never renders inside the iOS / Android
              Capacitor builds — those keep using `MobileBottomNav` exclusively. */}
          {!IS_NATIVE_APP && (
            <button
              data-testid="nav-menu-toggle"
              aria-label="Open navigation menu"
              onClick={() => setMenuOpen(true)}
              className="w-10 h-10 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
            >
              <Menu className="w-5 h-5 text-white" />
            </button>
          )}
        </div>
      </div>
      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
      {!IS_NATIVE_APP && (
        <WebSideMenu
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          token={token}
          onLogout={onLogout}
        />
      )}
    </header>
  );
}
// ===== Footer =====
export function Footer() {
  const { pathname } = useLocation();
  const isHomeRoute = pathname === '/' || pathname === '/welcome' || pathname === '/home';
  return (
    <footer data-testid="footer" className="relative border-t border-white/5 py-12 text-center">
      <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-[#94A3B8]">
        <div className="flex items-center gap-2">
          {isHomeRoute && <LogoIcon className="h-5 w-auto" />}
          <span>{isHomeRoute ? '- ' : ''}Your Feed Reinvented by AI</span>
        </div>
        <nav className="flex items-center gap-4">
          <Link
            data-testid="footer-link-privacy"
            to="/privacy"
            className="hover:text-white transition-colors"
          >
            Privacy Policy
          </Link>
          <span aria-hidden className="inline-block w-1 h-1 rounded-full bg-[#94A3B8]/60" />
          <Link
            data-testid="footer-link-terms"
            to="/terms"
            className="hover:text-white transition-colors"
          >
            Terms of Service
          </Link>
        </nav>
        <span>(c) {new Date().getFullYear()} ZenScroll. Made mindfully.</span>
      </div>
    </footer>
  );
}
